import { fetchWithAuth } from '../AuthService';
import { AI_SERVICE_BASE_URL } from '../AiApiService';

/**
 * Ai Document Images API
 * Upload & stitch multiple images into a single document (AI service proxy base).
 * Mirrors patterns used in AiDocumentProcessingApi (optional retry wrapper kept simple here).
 */
const retry = async (fn, attempts = 3, baseDelay = 600, op = 'image upload') => {
  let lastErr;
  for (let i=1;i<=attempts;i++) {
    try {
      const res = await fn();
      if (res?.ok) return res;
      const text = await (res?.text?.() || Promise.resolve(''));
      throw new Error(`HTTP ${res?.status}: ${text}`);
    } catch (e) {
      lastErr = e;
      if (i === attempts) break;
      const delay = baseDelay * Math.pow(2, i-1);
      console.warn(`DocumentImagesApi: ${op} failed attempt ${i}/${attempts} -> retrying in ${delay}ms`, e.message);
      await new Promise(r=>setTimeout(r, delay));
    }
  }
  throw lastErr;
};

export const DocumentImagesApi = {
  /**
   * Upload and stitch images into one document
   * Endpoint: POST /api/documents/upload-images
   * @param {File[]} images
   * @param {Object} options (title*, description, ocr=true, processType='none', skipProcessing=false, userId)
   * @returns {Promise<Response>} Response (caller parses JSON)
   */
  uploadCombinedImages: async (images, options = {}) => {
    const { title, description, ocr = true, processType = 'none', skipProcessing = false, userId, documentTypeId } = options;
    if (!images || !images.length) throw new Error('No images provided');
    if (!title || !title.trim()) throw new Error('Title required');

    const form = new FormData();
    console.debug('DocumentImagesApi.uploadCombinedImages: preparing FormData', {
      imageCount: images.length,
      images: images.map(f => ({ name: f.name, size: f.size, type: f.type })),
      options: { title, hasDescription: !!description, ocr, processType, skipProcessing, hasUserId: userId != null, documentTypeId }
    });
    images.forEach((file, idx) => form.append('images[]', file, file.name || `page_${idx+1}`));
    form.append('title', title.trim());
    form.append('ocr', ocr ? 'true' : 'false');
    form.append('processType', processType);
    form.append('skipProcessing', String(!!skipProcessing));
  if (description && description.trim()) form.append('description', description.trim());
  if (documentTypeId != null) form.append('documentTypeId', documentTypeId);
    if (userId != null) form.append('userId', userId);

    // Enumerate FormData keys for debugging (browser-only; harmless on server)
    try {
      const entries = [];
      for (const pair of form.entries()) {
        const [k, v] = pair;
        if (v instanceof File) {
          entries.push({ key: k, file: { name: v.name, size: v.size, type: v.type } });
        } else {
          entries.push({ key: k, value: String(v).slice(0,200) });
        }
      }
      console.debug('DocumentImagesApi.uploadCombinedImages: FormData entries', entries);
    } catch(e) {
      console.warn('DocumentImagesApi.uploadCombinedImages: Could not enumerate FormData entries', e);
    }

    return retry(() => fetchWithAuth(`${AI_SERVICE_BASE_URL}/api/documents/upload-images`, {
      method: 'POST',
      body: form,
    }), 3, 600, 'combined image upload');
  }
};
